import { useCallback, useEffect, useState } from "react";
import * as Tabs from "@radix-ui/react-tabs";
import moment from "moment-timezone";
import {
  ArrowRightIcon,
  ChevronRightIcon,
  PlusIcon,
} from "@radix-ui/react-icons";
import {
  ClassesService,
  ClassRow,
  OrganizationResponseForAdmins,
  WebPageService,
} from "client/openapi";
import {
  Button,
  ButtonColor,
  ButtonFill,
  ButtonSize,
  ExternalLinkButton,
} from "components/Button";
import ClassDialog from "components/Classes/ClassDialog";
import {
  dateRange,
  inZone,
  oneSessionLabel,
  weeklyLabel,
} from "components/Classes/classText";
import { errorText } from "components/Forms/formsApi";
import { Switch } from "components/Switch";
import OrgAdminPage from "pages/org-performance/OrgAdminPage";
import Notifications from "util/notifications";

type Tab = "current" | "past";

const isFull = (row: ClassRow) =>
  !!row.capacity && row.enrolled_count >= row.capacity;

/** "7 / 12" over a bar, or the count alone with no seat limit. */
function Seats({ row }: { row: ClassRow }) {
  if (!row.capacity)
    return (
      <span className="text-sm text-gray-700">
        {row.enrolled_count}{" "}
        <span className="text-xs text-gray-400">no limit</span>
      </span>
    );
  const full = isFull(row);
  return (
    <div className="flex items-center gap-3">
      <span className="w-12 shrink-0 text-sm text-gray-700">
        {row.enrolled_count} / {row.capacity}
      </span>
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-200">
        <div
          className={`h-full rounded-full ${
            full ? "bg-gray-400" : "bg-green-500"
          }`}
          style={{
            width: `${Math.min(
              100,
              (row.enrolled_count / row.capacity) * 100
            )}%`,
          }}
        />
      </div>
    </div>
  );
}

function ClassesList({
  organization,
}: {
  organization: OrganizationResponseForAdmins;
}) {
  const [tab, setTab] = useState<Tab>("current");
  const [rows, setRows] = useState<Record<Tab, ClassRow[] | undefined>>({
    current: undefined,
    past: undefined,
  });
  // Undefined: closed; null: New class; a number: that class.
  const [editing, setEditing] = useState<number | null>();
  const [publicPage, setPublicPage] = useState<{
    url: string;
    on: boolean;
    zone: string;
  }>();

  const load = useCallback(() => {
    (["current", "past"] as Tab[]).forEach((which) =>
      ClassesService.getClasses({
        orgId: organization.id,
        past: which === "past",
      })
        .then((loaded) => setRows((r) => ({ ...r, [which]: loaded })))
        .catch((e) =>
          Notifications.error(errorText(e, "Classes could not be loaded."))
        )
    );
  }, [organization.id]);
  useEffect(load, [load]);

  useEffect(() => {
    WebPageService.getOrganizationBookingSettings({ orgId: organization.id })
      .then((settings) =>
        setPublicPage({
          url: settings.custom_domain
            ? `https://${settings.custom_domain}/classes`
            : `${process.env.REACT_APP_FRONTEND_DOMAIN}/organizations/${organization.id}/classes`,
          on: settings.enabled,
          zone: settings.timezone,
        })
      )
      .catch(() => setPublicPage(undefined));
  }, [organization.id]);

  // One click opens or closes enrollment.
  const setOpen = (row: ClassRow, open: boolean) => {
    setRows((r) => ({
      ...r,
      current: r.current?.map((c) =>
        c.id === row.id ? { ...c, enrollment_open: open } : c
      ),
    }));
    ClassesService.updateClass({
      classId: row.id,
      requestBody: { enrollment_open: open },
    }).catch((e) => {
      Notifications.error(errorText(e, "Enrollment didn't change."));
      load();
    });
  };

  const shown = rows[tab];
  const nothingYet = rows.current?.length === 0 && rows.past?.length === 0;
  const browserZone = moment.tz.guess();

  const newClassButton = (
    <Button
      color={ButtonColor.GREEN}
      size={ButtonSize.SMALL}
      onClick={() => setEditing(null)}
    >
      <PlusIcon /> New class
    </Button>
  );

  return (
    <div className="py-6 px-7 rounded-xl bg-neutral-50">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-bold">Classes</h2>
        <div className="flex items-center gap-2">
          {publicPage && (
            <ExternalLinkButton
              href={publicPage.url}
              target="_blank"
              rel="noreferrer"
              color={ButtonColor.GREEN}
              fill={ButtonFill.HOLLOW}
              size={ButtonSize.SMALL}
            >
              Public page <ArrowRightIcon />
            </ExternalLinkButton>
          )}
          {!nothingYet && newClassButton}
        </div>
      </div>
      {publicPage && !publicPage.on && (
        <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Families can't see your classes yet. Turn on Online booking in
          Organization Settings, Booking pages.
        </p>
      )}

      {nothingYet ? (
        <div className="mt-10 mb-6 flex flex-col items-center text-center">
          <p className="text-lg font-semibold text-gray-900">No classes yet</p>
          <p className="mt-2 max-w-md text-sm text-gray-600">
            A class is a set of weekly sessions that students join together.
            Open it for enrollment and families can sign up from your public
            page.
          </p>
          <div className="mt-5">{newClassButton}</div>
        </div>
      ) : (
        <>
          <Tabs.Root value={tab} onValueChange={(v) => setTab(v as Tab)}>
            <Tabs.List
              className="mt-5 flex gap-7 border-b border-gray-200"
              aria-label="Classes"
            >
              {(["current", "past"] as Tab[]).map((which) => (
                <Tabs.Trigger
                  key={which}
                  value={which}
                  className="-mb-px flex items-center gap-2 border-b-[3px] border-transparent pb-2.5 text-[15px] font-medium text-gray-400 hover:text-gray-800 data-[state=active]:border-green-500 data-[state=active]:font-semibold data-[state=active]:text-neutral-900"
                >
                  {which === "current" ? "Current" : "Past"}
                  {rows[which] && (
                    <span className="rounded-full bg-gray-200 px-2 text-xs font-semibold leading-5 text-gray-600">
                      {rows[which]?.length}
                    </span>
                  )}
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Tabs.Root>

          {!shown ? (
            <div className="mt-6 animate-pulse space-y-3">
              <div className="h-10 rounded bg-gray-200" />
              <div className="h-10 rounded bg-gray-100" />
            </div>
          ) : shown.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">
              {tab === "current"
                ? "No classes with sessions still to come."
                : "Classes show here after their last session."}
            </p>
          ) : (
            <div className="mt-6 overflow-x-auto">
              <table className="w-full border-collapse bg-white rounded-lg">
                <thead>
                  <tr className="border-b-2 border-gray-300">
                    <th className="text-left p-3 text-sm font-bold">Class</th>
                    <th className="text-left p-3 text-sm font-bold">When</th>
                    <th className="text-left p-3 text-sm font-bold">Tutors</th>
                    <th className="text-left p-3 text-sm font-bold">
                      Students
                    </th>
                    <th className="text-left p-3 text-sm font-bold">
                      Enrollment
                    </th>
                    <th className="p-3" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((row) => (
                    <tr
                      key={row.id}
                      className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setEditing(row.id)}
                    >
                      <td className="p-3">
                        <p className="text-sm font-medium text-gray-900">
                          {row.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {row.subject.name}
                        </p>
                      </td>
                      <td className="p-3 whitespace-nowrap">
                        {oneSessionLabel(row, browserZone) ? (
                          <p className="text-sm text-gray-800">
                            {oneSessionLabel(row, browserZone)}
                          </p>
                        ) : (
                          <>
                            <p className="text-sm text-gray-800">
                              {weeklyLabel(
                                inZone(row.weekly_times, browserZone)
                              )}
                            </p>
                            <p className="text-xs text-gray-500">
                              {dateRange(row.first_date, row.last_date)}
                            </p>
                          </>
                        )}
                      </td>
                      <td className="p-3">
                        {row.tutors.length ? (
                          <span className="flex flex-wrap gap-1.5">
                            {row.tutors.map((t) => (
                              <span
                                key={t.id}
                                className="rounded-full bg-sky-100 px-2.5 py-0.5 text-xs font-semibold text-sky-800"
                              >
                                {t.first_name}
                              </span>
                            ))}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">
                            No tutor yet
                          </span>
                        )}
                      </td>
                      <td className="p-3">
                        <Seats row={row} />
                      </td>
                      <td className="p-3" onClick={(e) => e.stopPropagation()}>
                        {tab === "current" ? (
                          <span className="flex items-center gap-3">
                            <Switch
                              name={`class-open-${row.id}`}
                              checked={row.enrollment_open}
                              onCheckedChange={(open) => setOpen(row, open)}
                              label={`Enrollment for ${row.name}`}
                            >
                              {null}
                            </Switch>
                            <span className="text-sm text-gray-700">
                              {!row.enrollment_open
                                ? "Closed"
                                : isFull(row)
                                ? "Full"
                                : "Open"}
                            </span>
                          </span>
                        ) : (
                          <span className="text-sm text-gray-400">Ended</span>
                        )}
                      </td>
                      <td className="p-3 text-right text-gray-400">
                        <ChevronRightIcon />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {editing !== undefined && (
        <ClassDialog
          organization={organization}
          classId={editing ?? undefined}
          defaultZone={publicPage?.zone}
          onClose={() => setEditing(undefined)}
          onChanged={load}
        />
      )}
    </div>
  );
}

export default function ClassesPage() {
  return (
    <OrgAdminPage>
      {(organization) => <ClassesList organization={organization} />}
    </OrgAdminPage>
  );
}
